// Fill the "Details of Participants" list from an Excel (.xlsx) sheet, and download the
// template to fill in. Shared by the apply form and both edit pages; each row is checked
// with the same rules as the form fields, and the camp's participant limit still applies.
import readXlsxFile from "read-excel-file";
import writeXlsxFile from "write-excel-file";
import { applicationRules } from "@/components/Common/applicationRulesMixin";

const GENDERS = {
  male: "Male",
  m: "Male",
  female: "Female",
  f: "Female",
  transgender: "Transgender",
  trans: "Transgender",
  t: "Transgender",
};

const pad = (n) => String(n).padStart(2, "0");
const display = (iso) => iso.split("-").reverse().join("/");

// "15/06/2012", "15-06-2012", "2012-06-15", an Excel date cell or a serial number -> "2012-06-15"
function toIsoDate(cell) {
  let y;
  let m;
  let d;
  if (cell instanceof Date) {
    // Excel dates arrive as UTC midnight
    y = cell.getUTCFullYear();
    m = cell.getUTCMonth() + 1;
    d = cell.getUTCDate();
  } else if (typeof cell === "number") {
    const dt = new Date(Math.round((cell - 25569) * 86400 * 1000));
    y = dt.getUTCFullYear();
    m = dt.getUTCMonth() + 1;
    d = dt.getUTCDate();
  } else {
    const s = String(cell || "").trim();
    let match = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(s);
    if (match) {
      d = +match[1];
      m = +match[2];
      y = +match[3];
    } else if ((match = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s))) {
      y = +match[1];
      m = +match[2];
      d = +match[3];
    } else {
      return null;
    }
  }
  const check = new Date(y, m - 1, d);
  if (y < 1900 || check.getFullYear() !== y || check.getMonth() !== m - 1 || check.getDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

const isEmptyCell = (cell) => cell === null || cell === undefined || String(cell).trim() === "";

export default {
  methods: {
    // The participants list of this form (the edit pages keep theirs in memberDetails2)
    participantsKey() {
      return Object.prototype.hasOwnProperty.call(this.$data, "memberDetails2") ? "memberDetails2" : "memberDetails";
    },

    downloadParticipantTemplate() {
      const head = (value) => ({ value, fontWeight: "bold", backgroundColor: "#d7efec" });
      const total = this.memberHardLimit
        ? `This camp takes up to ${this.memberLimit} participants, plus ${this.extraMembers} additional names (${this.memberHardLimit} in all).`
        : "";
      const sheet = [
        [head("Name"), head("Date of Birth (DD/MM/YYYY)"), head("Gender (Male / Female / Transgender)")],
        [{ value: "Example - Anu Raj" }, { value: "15/06/2012" }, { value: "Female" }],
        [{ value: "Example - Arun Kumar" }, { value: "03/11/2011" }, { value: "Male" }],
      ];
      const help = [
        [{ value: "How to fill the participants sheet", fontWeight: "bold" }],
        [{ value: "1. Use the Participants sheet. One participant per row, starting below the heading." }],
        [{ value: "2. Delete the two example rows (they start with the word Example)." }],
        [{ value: "3. Name: letters, spaces, hyphens and dots only." }],
        [{ value: "4. Date of Birth: DD/MM/YYYY. A participant must be at least 5 years old." }],
        [{ value: "5. Gender: Male, Female or Transgender." }],
        [{ value: "6. Group leaders are already in the list, so do not add them again." }],
        [{ value: total }],
        [{ value: "7. Save as .xlsx, then use Upload Excel on the application form." }],
      ].filter((row) => row[0].value);
      return writeXlsxFile([sheet, help], {
        sheets: ["Participants", "How to fill"],
        columns: [
          [{ width: 32 }, { width: 28 }, { width: 38 }],
          [{ width: 95 }],
        ],
        fileName: "Participants-template.xlsx",
      });
    },

    chooseParticipantExcel() {
      if (this.$refs.participantExcel) this.$refs.participantExcel.click();
    },

    async onParticipantExcel(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      input.value = "";
      if (!file) return;
      if (!/\.xlsx$/i.test(file.name)) {
        this.msg = "Please choose an Excel file saved as .xlsx.";
        this.showSnackBar = true;
        return;
      }

      let rows;
      try {
        rows = await readXlsxFile(file);
      } catch (err) {
        console.error(err);
        this.msg = "Couldn't read that file. Use the Excel template and save it as .xlsx.";
        this.showSnackBar = true;
        return;
      }

      // Columns come from the heading row when there is one, otherwise A, B, C
      let col = { name: 0, dob: 1, gender: 2 };
      let first = 0;
      const heading = (rows[0] || []).map((cell) => String(cell || "").toLowerCase());
      if (heading.some((text) => /name|birth|dob|gender/.test(text))) {
        first = 1;
        const find = (re, fallback) => {
          const at = heading.findIndex((text) => re.test(text));
          return at === -1 ? fallback : at;
        };
        col = { name: find(/name/, 0), dob: find(/birth|dob/, 1), gender: find(/gender|sex/, 2) };
      }

      const maxDob = typeof this.calculateMaxDate === "function" ? this.calculateMaxDate() : null;
      const valid = [];
      const problems = [];
      for (let i = first; i < rows.length; i++) {
        const row = rows[i] || [];
        const name = isEmptyCell(row[col.name]) ? "" : String(row[col.name]).trim();
        if (!name && isEmptyCell(row[col.dob]) && isEmptyCell(row[col.gender])) continue; // blank row
        if (/^example\b/i.test(name)) continue; // the template's sample rows
        const line = i + 1;

        const nameProblem = applicationRules.personName.map((rule) => rule(name)).find((r) => r !== true);
        if (nameProblem) {
          problems.push(`Row ${line}: ${nameProblem}.`);
          continue;
        }
        const dob = toIsoDate(row[col.dob]);
        if (!dob) {
          problems.push(`Row ${line}: Date of Birth must be DD/MM/YYYY.`);
          continue;
        }
        if (maxDob && dob > maxDob) {
          problems.push(`Row ${line}: a participant must be born on or before ${display(maxDob)}.`);
          continue;
        }
        const gender = GENDERS[String(row[col.gender] || "").trim().toLowerCase()];
        if (!gender) {
          problems.push(`Row ${line}: Gender must be Male, Female or Transgender.`);
          continue;
        }
        valid.push({ memberName: name, memberDob: dob, memberGender: gender });
      }

      // Add after the participants already entered (blank rows are replaced)
      const key = this.participantsKey();
      const isBlank = (m) => !m.isFromLeader && !String(m.memberName || "").trim() && !m.memberDob && !m.memberGender;
      const kept = (this[key] || []).filter((m) => !isBlank(m));
      const room = this.memberHardLimit ? Math.max(0, this.memberHardLimit - kept.length) : valid.length;
      const toAdd = valid.slice(0, room);
      const overLimit = valid.length - toAdd.length;
      if (toAdd.length) {
        this[key] = kept.concat(
          toAdd.map((m) => ({
            ...m,
            ...(key === "memberDetails2" ? { foodPreference: "" } : {}),
            isFromLeader: false,
          }))
        );
      }

      const notes = problems.slice(0, 6);
      if (problems.length > 6) notes.push(`and ${problems.length - 6} more.`);
      if (overLimit) {
        notes.push(
          `${overLimit} more row${overLimit === 1 ? "" : "s"} not added: this camp takes up to ${this.memberLimit} participants plus ${this.extraMembers} additional names.`
        );
      }
      const added = `${toAdd.length} participant${toAdd.length === 1 ? "" : "s"} added.`;
      if (this.$appAlert) {
        this.$appAlert({
          type: toAdd.length && !notes.length ? "success" : "warning",
          title: toAdd.length ? "Participants imported" : "No participants added",
          message: [added, notes.length ? "Please check: " + notes.join(" ") : ""].filter(Boolean).join(" "),
        });
      } else {
        this.msg = [added, ...notes].join(" ");
        this.showSnackBar = true;
      }
    },
  },
};
